import { inject, NgModule } from '@angular/core';
import { Route, Routes, RouterModule, UrlSegment } from '@angular/router';

import {
    BuilderPageComponent,
    builderPageCanMatch,
} from '@jht/brand-page-builder/core';

import { RegLangGuard } from './shared/guards/reglang.guard';
import { PageGuard } from './shared/guards/page.guard';
import { AnalyticsGuard } from './shared/guards/analytics.guard';
import { EmptyLayoutComponent } from './core/components/empty-layout/empty-layout.component';
import { WrappedLayoutComponent } from './core/components/wrapped-layout/wrapped-layout.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { ProductComponent } from './product/product.component';
import { productResolver } from './product/shared/product.resolver';
import { catalogResolver } from './catalog/catalog.resolver';
import { urlMatcherGuard } from './shared/guards/url-matcher.guard';
import { ContentService } from './shared/content';
import { isCurrentPageInPagesOrProductsComponent } from './shared/utils/utils';
import { Config } from './shared/config';
import { templateGuard } from './single-pages/template.guard';
import { matchInsights } from './insights/insights-match.guard';

const matchCategory = (route: Route, segments: UrlSegment[]) => {
    const validCategories = ['strength', 'cardio'];
    const contentService = inject(ContentService);
    const config = inject(Config);
    const pageMatches: boolean = isCurrentPageInPagesOrProductsComponent(
        contentService.pagesData,
        segments,
        config.getCurrentLanguage(),
    );

    return validCategories.includes(segments[0].path) && pageMatches;
};

const matchSellSheet = (_: Route, segments: UrlSegment[]) => {
    const pdbId = segments[0].path;
    const consoleId =
        segments[1].path === 'sellsheet' ? null : segments[1].path;
    const isNumber = (val: string) => /^\d+$/.test(val);

    if (!isNumber(pdbId) || (consoleId && !isNumber(consoleId))) {
        return false;
    }
    return true;
};

// This is the page builder editor route
const pageBuilderRoute: Route = {
    path: 'page-builder',
    loadChildren: () =>
        import('@jht/brand-page-builder').then((m) => m.PAGE_BUILDER_ROUTES),
};

const _routes: Routes = [
    // This is the renderer route for the built pages to be displayed
    {
        path: '**',
        component: BuilderPageComponent,
        canMatch: [builderPageCanMatch],
    },
    {
        path: ':category/:modality/:product',
        pathMatch: 'full',
        canMatch: [matchCategory],
        component: ProductComponent,
        canActivate: [RegLangGuard],
        resolve: {
            productData: productResolver,
        },
    },
    {
        path: 'contact-us',
        loadChildren: () =>
            import('./contact-us/contact-us.module').then(
                (m) => m.ContactUsModule,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'cardio',
        canMatch: [matchCategory],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./cardio/cardio.component').then(
                        (m) => m.CardioComponent,
                    ),
            },
            {
                path: 'entertainment-consoles',
                loadComponent: () =>
                    import(
                        './cardio/entertainment-consoles/entertainment-consoles.component'
                    ).then((m) => m.EntertainmentConsolesComponent),
            },
        ],
        canActivate: [RegLangGuard],
    },
    {
        path: 'strength',
        loadComponent: () =>
            import('./strength/strength.component').then(
                (m) => m.StrengthComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'support',
        loadComponent: () =>
            import('./support/support.component').then(
                (m) => m.SupportComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'about-us',
        loadComponent: () =>
            import('./about-us/about-us.component').then(
                (m) => m.AboutUsComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'dealers',
        loadComponent: () =>
            import('./dealers/dealers.component').then(
                (m) => m.DealersComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'design-studio',
        loadComponent: () =>
            import('./design-studio/design-studio.component').then(
                (m) => m.DesignStudioComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'markets',
        loadComponent: () =>
            import('./markets/markets.component').then(
                (m) => m.MarketsComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'insights',
        loadChildren: () =>
            import('./insights/insights.module').then((m) => m.InsightsModule),
        canMatch: [matchInsights],
        canActivate: [RegLangGuard],
    },
    {
        path: 'impressum',
        loadComponent: () =>
            import('./impressum/impressum.component').then(
                (m) => m.ImpressumComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'legal',
        loadComponent: () =>
            import('./legal/legal.component').then((m) => m.LegalComponent),
        canActivate: [RegLangGuard],
    },
    {
        path: 'local-offices',
        loadComponent: () =>
            import('./local-offices/local-offices.component').then(
                (m) => m.LocalOfficesComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: 'search',
        loadComponent: () =>
            import('./search/search.component').then((m) => m.SearchComponent),
        canActivate: [RegLangGuard],
    },
    {
        path: 'catalog',
        loadComponent: () =>
            import('./catalog/catalog.component').then(
                (m) => m.CatalogComponent,
            ),
        canActivate: [RegLangGuard],
        resolve: {
            catalogData: catalogResolver,
        },
    },
    {
        path: 'style-guide',
        loadComponent: () =>
            import('./style-guide/style-guide.component').then(
                (m) => m.StyleGuideComponent,
            ),
        canActivate: [RegLangGuard],
    },
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
            import('./homepage/homepage.component').then(
                (m) => m.HomepageComponent,
            ),
        canActivate: [RegLangGuard],
    },

    {
        canMatch: [templateGuard],
        path: '**',
        loadComponent: () =>
            import('./single-pages/mega/mega-template.component').then(
                (m) => m.MegaTemplateComponent,
            ),
    },
];

const emptyLayoutRoutes: Routes = [
    {
        path: ':pdb_id/:console/sellsheet',
        canMatch: [matchSellSheet],
        loadComponent: () =>
            import('./sellsheet/sellsheet.component').then(
                (m) => m.SellsheetComponent,
            ),
    },
    {
        path: ':pdb_id/sellsheet',
        canMatch: [matchSellSheet],
        loadComponent: () =>
            import('./sellsheet/sellsheet.component').then(
                (m) => m.SellsheetComponent,
            ),
    },
];

const wrappedLayoutRoutesWithLang: Route = {
    path: ':language',
    data: { route: '{language:(?:[A-Za-z]{3})}' },
    component: WrappedLayoutComponent,
    children: _routes,
    canMatch: [urlMatcherGuard],
    canActivateChild: [PageGuard, AnalyticsGuard],
};

const emptyLayoutRoutesWithLanguage = {
    path: ':language',
    canActivateChild: [AnalyticsGuard],
    name: 'emptyLayoutRoutesWithLanguage',
    canMatch: [urlMatcherGuard],
    data: { route: '{language:(?:[A-Za-z]{3})}' },
    component: EmptyLayoutComponent,
    children: emptyLayoutRoutes,
};

const catchAllRoute: Route = {
    path: '**',
    component: WrappedLayoutComponent,
    canActivate: [RegLangGuard, PageGuard],
    children: [
        {
            path: '**',
            component: PageNotFoundComponent,
            data: { notFound: true },
        },
    ],
};

export const routes: Routes = [
    pageBuilderRoute,
    wrappedLayoutRoutesWithLang,
    emptyLayoutRoutesWithLanguage,
    catchAllRoute,
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            initialNavigation: 'enabledNonBlocking',
            bindToComponentInputs: true,
            anchorScrolling: 'enabled',
            scrollPositionRestoration: 'top',

            //enableTracing: true,
            errorHandler: (error) => {
                console.error('Routing error:', error);
            },
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
